import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { findCollectionById, getPiecesByCollection, CollectionItem, JewelryItem } from '../data/jewelryData.ts';

const CollectionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [collection, setCollection] = useState<CollectionItem | null>(null);
  const [pieces, setPieces] = useState<JewelryItem[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentPieceSlide, setCurrentPieceSlide] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const piecesCarouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const piecesTouchStartX = useRef<number>(0);
  const piecesTouchEndX = useRef<number>(0);

  useEffect(() => {
    if (id) {
      const data = findCollectionById(id);
      if (data) {
        // Ordena as imagens alfabeticamente pelo nome do arquivo (1.jpg, 2.jpg, ...)
        const sortedImages = [...data.images].sort((a, b) => {
          const numA = parseInt(a.substring(a.lastIndexOf('/') + 1, a.lastIndexOf('.')));
          const numB = parseInt(b.substring(b.lastIndexOf('/') + 1, b.lastIndexOf('.')));
          return numA - numB;
        });
        setCollection({ ...data, images: sortedImages });
        setCurrentImageIndex(0);
        
        // Busca as peças da coleção
        const collectionPieces = getPiecesByCollection(id);
        setPieces(collectionPieces);
      } else {
        setCollection(null);
        setPieces([]);
      }
    }
  }, [id]);

  // Atualizar estado mobile quando a tela redimensionar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!collection) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-3xl font-bold mb-6">Coleção não encontrada</h1>
        <p className="mb-8">A coleção que você está procurando não existe em nosso catálogo.</p>
        <Link to="/catalogo" className="btn btn-primary">
          Voltar ao Catálogo
        </Link>
      </div>
    );
  }

  const handleThumbnailClick = (index: number) => {
    if (index === currentImageIndex) return;
    
    // Determina a direção da animação
    const direction = index > currentImageIndex ? 'left' : 'right';
    setSlideDirection(direction);
    
    // Define a próxima imagem como variável CSS
    if (containerRef.current) {
      containerRef.current.style.setProperty('--next-image', `url(${collection.images[index]})`);
    }
    
    // Remove a classe de animação e atualiza a imagem após a transição
    setTimeout(() => {
      setCurrentImageIndex(index);
      setSlideDirection(null);
      if (containerRef.current) {
        containerRef.current.style.removeProperty('--next-image');
      }
    }, 500);
  };

  const handlePrevImage = () => {
    const prevIndex = (currentImageIndex - 1 + collection.images.length) % collection.images.length;
    handleThumbnailClick(prevIndex);
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % collection.images.length;
    handleThumbnailClick(nextIndex);
  };

  // Funções para manipular gestos touch
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    
    const touchDiff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Distância mínima para considerar um swipe
    
    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe para a esquerda - próxima imagem
        handleNextImage();
      } else {
        // Swipe para a direita - imagem anterior
        handlePrevImage();
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || isMobile) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsZooming(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsZooming(false);
    }
  };

  // Funções para o carrossel de peças
  const itemsPerGroup = isMobile ? 1 : 3;
  const pieceGroups = [];
  for (let i = 0; i < pieces.length; i += itemsPerGroup) {
    pieceGroups.push(pieces.slice(i, i + itemsPerGroup));
  }

  const nextPieceSlide = () => {
    setCurrentPieceSlide((prev) => (prev + 1) % pieceGroups.length);
  };

  const prevPieceSlide = () => {
    setCurrentPieceSlide((prev) => (prev - 1 + pieceGroups.length) % pieceGroups.length);
  };

  // Funções para manipular gestos touch no carrossel de peças
  const handlePiecesTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    piecesTouchStartX.current = e.touches[0].clientX;
  };

  const handlePiecesTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    piecesTouchEndX.current = e.touches[0].clientX;
  };

  const handlePiecesTouchEnd = () => {
    if (!isMobile) return;
    
    const touchDiff = piecesTouchStartX.current - piecesTouchEndX.current;
    const minSwipeDistance = 50; // Distância mínima para considerar um swipe
    
    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe para a esquerda - próximo slide
        nextPieceSlide();
      } else {
        // Swipe para a direita - slide anterior
        prevPieceSlide();
      }
    }
  };

  return (
    <div className="collection-detail-page py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          {/* Coluna da Galeria de Imagens */}
          <div className="image-gallery-section">
            {/* Imagem Grande em Destaque */}
            <div 
              ref={containerRef}
              className={`featured-image-container mb-4 relative overflow-hidden rounded-lg aspect-square bg-white ${!isMobile ? 'cursor-crosshair' : ''}`}
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={`image-slide-container ${slideDirection ? `slide-${slideDirection}` : ''}`}>
                <img
                  ref={imageRef}
                  src={collection.images[currentImageIndex]}
                  alt={`${collection.name} - Imagem ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain transition-all duration-300 ease-in-out"
                  key={currentImageIndex}
                  onMouseMove={handleMouseMove}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              </div>
              
              {/* Botões de Navegação */}
              {collection.images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-2 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Imagem anterior"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-2 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Próxima imagem"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Círculo de Zoom */}
              {isZooming && (
                <div 
                  className="zoom-circle"
                  style={{
                    left: `${zoomPosition.x}%`,
                    top: `${zoomPosition.y}%`,
                    backgroundImage: `url(${collection.images[currentImageIndex]})`,
                    backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                  }}
                />
              )}
            </div>

            {/* Carrossel de Miniaturas */}
            {collection.images.length > 1 && (
              <div className="thumbnail-carousel flex space-x-3 overflow-x-auto p-4 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                {collection.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => handleThumbnailClick(index)}
                    className={`thumbnail-item flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${ 
                      currentImageIndex === index ? 'border-azul-majestoso scale-105' : 'border-transparent hover:border-azul-mitologico'
                    }`}
                    aria-label={`Ver imagem ${index + 1}`}
                  >
                    <img
                      src={image}
                      alt={`${collection.name} - Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Coluna de Descrição */}
          <div className="description-section">
            <h1 className="text-3xl md:text-4xl font-bold text-azul-majestoso mb-4">
              {collection.name}
            </h1>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              {collection.fullDescription}
            </p>

            <div className="space-y-3 text-gray-600">
              <div className="flex items-center">
                <span className="font-semibold w-32">Peças:</span>
                <span>{collection.content}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Lançamento:</span>
                <span>{collection.releaseDate}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Criador:</span>
                <span>{collection.creator}</span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contato" className="btn btn-primary">
                Solicitar Informações
              </Link>
              <Link to="/catalogo" className="btn btn-primary">
                Voltar ao Catálogo
              </Link>
            </div>
          </div>
        </div>

        {/* Seção de Peças da Coleção */}
        <div className="pieces-section">
          <h2 className="text-2xl md:text-3xl font-bold text-azul-majestoso mb-8 text-center">
            Peças
          </h2>
          
          {pieceGroups.length > 0 && (
            <div className="relative">
              {/* Carrossel Container */}
              <div 
                className="overflow-hidden"
                ref={piecesCarouselRef}
                onTouchStart={handlePiecesTouchStart}
                onTouchMove={handlePiecesTouchMove}
                onTouchEnd={handlePiecesTouchEnd}
              >
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentPieceSlide * 100}%)` }}
                >
                  {pieceGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="w-full flex-shrink-0">
                      <div className={`grid gap-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                        {group.map((piece) => (
                          <Link
                            key={piece.id}
                            to={`/joia/${piece.id}`}
                            className="piece-card group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                          >
                            <div className="aspect-square overflow-hidden">
                              <img
                                src={piece.images[0]}
                                alt={piece.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            </div>
                            <div className="p-6 text-center">
                              <h3 className="text-xl font-bold text-azul-majestoso mb-2 group-hover:text-azul-mitologico transition-colors duration-200">
                                {piece.name}
                              </h3>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navegação do Carrossel de Peças */}
              {pieceGroups.length > 1 && (
                <>
                  <button
                    onClick={prevPieceSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-3 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Slide anterior"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextPieceSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-3 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Próximo slide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Indicadores de slide */}
                  <div className="flex justify-center mt-8 space-x-2">
                    {pieceGroups.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPieceSlide(index)}
                        className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                          currentPieceSlide === index ? 'bg-azul-majestoso' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Ir para slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionDetail;

